import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDrag } from 'react-dnd';

import Flyer from './Flyer';
import FlyerTemplatePreview from '../templates/FlyerTemplatePreview';
import { useProductSearch } from '../../hooks/useProductSearch';
import { useProductExtraction } from '../../hooks/useProductExtraction';
import DragContext from '../../context/DragContext';
import { useAppState } from '../../hooks/useAppState';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StagingArea from './StagingArea';
import UploadPlansTab from './UploadPlansTab';
import './AddFlyer.css';

// Product Card Component - Figma design style
function ProductCard({ product, onDoubleClick, hasSelection, onAddToStaging, isStaged }) {
  const setIsDragging = useContext(DragContext);

  const [{ isDragging }, drag] = useDrag({
    type: 'product',
    item: () => {
      setIsDragging(true);
      return {
        type: 'product',
        id: product.retailer_sku_id,
        originalPrice: product.original_price,
        promoStartDate: product.promo_start_date,
        promoEndDate: product.promo_end_date,
        promoType: product.promo_type,
        retailerSkuId: product.retailer_sku_id,
        variantId: product.variant_id,
        promoPrice: product.promo_price,
        inventory: product.inventory,
        cleanedImgLargeSrc: product.cleaned_img_large_src || product.original_img_large_src || product.img_large_src || product.image_url,
        cleanedImgSmallSrc: product.cleaned_img_small_src,
        originalImgLargeSrc: product.original_img_large_src || product.img_large_src || product.image_url,
        name: product.product_name,
        productDescription: product.product_description,
        category: product.category,
        subCategory1: product.sub_category_1,
        subCategory2: product.sub_category_2,
        subCategory3: product.sub_category_3,
        brand: product.brand,
        limit_qty: product.limit_qty,
        priceUnit: product.price_unit
      };
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: () => {
      setIsDragging(false);
    },
  });

  return (
    <div
      ref={drag}
      className="product-card"
      style={{ opacity: isDragging ? 0.5 : 1, position: 'relative' }}
    >
      <div className="product-card-header">
        <p className="product-card-name">{product.product_name}</p>
      </div>
      <div className="product-card-footer">
        <span className="product-card-sku">SKU: {product.retailer_sku_id}</span>
        <div className="product-card-prices">
          <span className="product-card-promo">${product.promo_price}</span>
          <span className="product-card-original">${product.original_price}</span>
        </div>
      </div>
      {hasSelection && (
        <button
          onClick={(e) => { e.stopPropagation(); onDoubleClick && onDoubleClick(product); }}
          style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.04)',
            border: '2px solid #ff8264', borderRadius: 'inherit', cursor: 'pointer',
            zIndex: 2,
          }}
        />
      )}
      {onAddToStaging && (
        <button
          className={`product-card-stage-btn ${isStaged ? 'staged' : ''}`}
          onClick={(e) => { e.stopPropagation(); onAddToStaging(product); }}
          title={isStaged ? 'Already in staging' : 'Add to staging'}
        >
          {isStaged ? '✓' : '+'}
        </button>
      )}
    </div>
  );
}

// Icons as inline SVGs
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9,18 15,12 9,6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15,18 9,12 15,6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

function AddFlyer() {
  const { getAccessTokenSilently } = useAuth0();
  const { organizationId, selectedLocation, country } = useAppState();
  const notification = useNotification();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [flyerTemplates, setFlyerTemplates] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [createdFlyer, setCreatedFlyer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [highlightedTemplateId, setHighlightedTemplateId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const flyerRef = useRef();
  const [isProductDragging, setIsProductDragging] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showCenterGuides, setShowCenterGuides] = useState(false);
  const [flyerPageIndex, setFlyerPageIndex] = useState(0);
  const [flyerTotalPages, setFlyerTotalPages] = useState(1);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Sidebar tabs & staging
  const [sidebarTab, setSidebarTab] = useState('search'); // 'search' | 'upload'
  const [stagedProducts, setStagedProducts] = useState([]);
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  // Product extraction hook
  const {
    files: uploadFiles,
    addFiles: addUploadFiles,
    removeFile: removeUploadFile,
    clearFiles: clearUploadFiles,
    isExtracting,
    extractProducts,
    extractedProducts,
    clearExtracted,
    resolveProduct,
    resolveGlobalProduct,
    dismissCandidates,
    addToCatalog,
    error: extractionError,
  } = useProductExtraction(selectedLocation, country);

  const addToStaging = useCallback((product) => {
    setStagedProducts(prev => {
      const id = product.retailer_sku_id || product.id;
      if (prev.some(p => (p.retailer_sku_id || p.id) === id)) return prev;
      return [...prev, product];
    });
  }, []);

  const addAllToStaging = useCallback((products) => {
    setStagedProducts(prev => {
      const existingIds = new Set(prev.map(p => p.retailer_sku_id || p.id));
      const newProducts = products.filter(p => !existingIds.has(p.retailer_sku_id || p.id));
      return [...prev, ...newProducts];
    });
  }, []);

  const removeFromStaging = useCallback((index) => {
    setStagedProducts(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearStaging = useCallback(() => {
    setStagedProducts([]);
  }, []);

  const handleAutoFill = useCallback(async () => {
    if (stagedProducts.length === 0 || !flyerRef.current?.autoFillProducts) return;
    setIsAutoFilling(true);
    try {
      // Format products for auto-fill (camelCase fields matching buildEnhancedShapes expectations)
      const productsForFill = stagedProducts.map(p => ({
        name: p.product_name || p.name,
        promoPrice: p.sale_price || p.promo_price || p.promoPrice,
        originalPrice: p.regular_price || p.original_price || p.originalPrice,
        priceUnit: p.price_unit || p.priceUnit,
        productDescription: p.product_description || p.productDescription,
        limit_qty: p.limit_qty,
        promoType: p.promo_type || p.promoType,
        cleanedImgLargeSrc: p.cleaned_img_large_src || p.original_img_large_src || p.img_large_src || p.image_url || p.cleanedImgLargeSrc,
        originalImgLargeSrc: p.original_img_large_src || p.img_large_src || p.image_url || p.originalImgLargeSrc,
        category: p.category,
      }));

      console.log('[AUTO-FILL] productsForFill:', productsForFill);
      const placedCount = flyerRef.current.autoFillProducts(productsForFill);
      if (placedCount > 0) {
        notification.success(`Placed ${placedCount} product(s) on the flyer`);
        setStagedProducts([]);
      } else {
        notification.warning('No empty cells available — products were not placed');
      }
    } catch (err) {
      console.error('Auto-fill failed:', err);
      notification.error('Failed to auto-fill products');
    } finally {
      setIsAutoFilling(false);
    }
  }, [stagedProducts, notification]);

  // Selected product in cell (for replace-on-double-click)
  const [selectedCellProduct, setSelectedCellProduct] = useState(null); // { cellIndex, productIndex }

  const handleSelectCellProduct = (cellIndex, productIndex) => {
    if (cellIndex === null) {
      setSelectedCellProduct(null);
    } else {
      setSelectedCellProduct({ cellIndex, productIndex });
    }
  };

  const handleReplaceCellProduct = (product) => {
    if (!selectedCellProduct || !flyerRef.current?.replaceCellProduct) return;
    const { cellIndex, productIndex } = selectedCellProduct;
    flyerRef.current.replaceCellProduct(cellIndex, productIndex, {
      name: product.product_name,
      promoPrice: product.promo_price,
      originalPrice: product.original_price,
      priceUnit: product.price_unit,
      productDescription: product.product_description,
      limit_qty: product.limit_qty,
      promoType: product.promo_type,
      cleanedImgLargeSrc: product.cleaned_img_large_src || product.original_img_large_src || product.img_large_src || product.image_url,
      originalImgLargeSrc: product.original_img_large_src || product.img_large_src || product.image_url,
    });
    setSelectedCellProduct(null);
  };

  // Form fields for Step 1
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState({});

  // Product search
  const {
    searchKeywords,
    setSearchKeywords,
    searchResultsCount,
    currentPage,
    isSearching,
    searchProducts,
    getCurrentPageProducts,
    nextPage,
    prevPage,
    totalPages
  } = useProductSearch(100, selectedLocation);


  const steps = [
    { number: 1, name: 'Flyer Details', description: 'Enter basic information' },
    { number: 2, name: 'Select Template', description: 'Choose a layout' },
    { number: 3, name: 'Add Products', description: 'Drag products to cells' }
  ];

  // Parse dates in a timezone-safe way (treat as local date, not UTC)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const str = typeof dateStr === 'string' ? dateStr : dateStr.toISOString?.() || String(dateStr);
    const [year, month, day] = str.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Load flyer for editing
  useEffect(() => {
    if (location.state && location.state.flyer) {
      const flyer = location.state.flyer;
      const startDateObj = parseLocalDate(flyer.start_date);
      const endDateObj = parseLocalDate(flyer.end_date);

      setTitle(flyer.flyer_title || '');
      // Format as YYYY-MM-DD for input fields
      const formatDateForInput = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };
      setStartDate(formatDateForInput(startDateObj));
      setEndDate(formatDateForInput(endDateObj));
      setSelectedTemplate(flyer.templateId);
      setIsEditing(true);

      setCreatedFlyer({
        ...flyer,
        start_date: startDateObj,
        end_date: endDateObj
      });
      setCurrentStep(3);
    }
  }, [location]);

  // Fetch flyer templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!organizationId) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyertemplates/organization/${organizationId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setFlyerTemplates(response.data);
      } catch (error) {
        console.error('Failed to fetch flyer templates:', error);
      }
    };

    fetchTemplates();
  }, [getAccessTokenSilently, organizationId]);

  const validateStep1 = () => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!title.trim()) {
      newErrors.title = 'Flyer title is required';
    }
    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    } else if (parseLocalDate(startDate) < today) {
      newErrors.startDate = 'Start date cannot be in the past';
    }
    if (!endDate) {
      newErrors.endDate = 'End date is required';
    } else if (startDate && parseLocalDate(endDate) < parseLocalDate(startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Next = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setHighlightedTemplateId(templateId);
  };

  const handleStep2Next = async () => {
    if (!highlightedTemplateId) {
      notification.warning('Please select a template to continue');
      return;
    }

    setSelectedTemplate(highlightedTemplateId);
    setIsCreating(true);

    try {
      const token = await getAccessTokenSilently();
      const flyerData = {
        flyer_title: title,
        start_date: startDate,
        end_date: endDate,
        template_id: highlightedTemplateId,
        location_id: selectedLocation
      };

      const response = await axios.post(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyers/`, flyerData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const newFlyer = response.data.result;
      setCreatedFlyer({
        ...newFlyer,
        start_date: parseLocalDate(newFlyer.start_date),
        end_date: parseLocalDate(newFlyer.end_date)
      });
      setIsEditing(true);
      setCurrentStep(3);

      notification.success('Flyer created successfully!');
    } catch (error) {
      console.error('Error creating flyer:', error);
      notification.error('Failed to create flyer. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchProducts();
    setShowSearchResults(true);
  };

  const handleCloseSearchResults = () => {
    setShowSearchResults(false);
  };

  const handleCancel = () => {
    navigate('/flyers');
  };

  const handleSave = async () => {
    if (flyerRef.current && flyerRef.current.saveFlyer) {
      // Temporarily switch to page 0 for preview image generation
      const originalPageIndex = flyerPageIndex;
      if (flyerPageIndex !== 0) {
        setFlyerPageIndex(0);
        // Wait for React to re-render with page 0
        await new Promise(r => setTimeout(r, 300));
      }

      await flyerRef.current.saveFlyer();

      // Restore original page
      if (originalPageIndex !== 0) {
        setFlyerPageIndex(originalPageIndex);
      }
    }
  };

  const handleSaveAndClose = async () => {
    if (flyerRef.current && flyerRef.current.saveFlyer) {
      // Temporarily switch to page 0 for preview image generation
      const originalPageIndex = flyerPageIndex;
      if (flyerPageIndex !== 0) {
        setFlyerPageIndex(0);
        // Wait for React to re-render with page 0
        await new Promise(r => setTimeout(r, 300));
      }

      await flyerRef.current.saveFlyer();

      // Restore original page (though we're closing anyway)
      if (originalPageIndex !== 0) {
        setFlyerPageIndex(originalPageIndex);
      }
      handleCancel();
    }
  };

  const handleDownload = () => {
    if (flyerRef.current && flyerRef.current.downloadFlyer) {
      flyerRef.current.downloadFlyer();
    }
  };

  const handlePublish = async () => {
    if (flyerRef.current && flyerRef.current.publishFlyer) {
      await flyerRef.current.publishFlyer();
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    if (!createdFlyer || !createdFlyer._id) return;

    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyers/${createdFlyer._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      notification.success('Flyer deleted successfully!');
      navigate('/flyers');
    } catch (error) {
      console.error('Error deleting flyer:', error);
      notification.error('Failed to delete flyer.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <DragContext.Provider value={setIsProductDragging}>
      <div className="add-flyer-wizard">

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete this flyer?"
          description="This action cannot be undone."
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Step Indicator Header */}
        <div className="wizard-header">
          <div className="wizard-header-content">
            <h1 className="wizard-title">
              {isEditing ? 'Edit Flyer' : 'Create New Flyer'}
              {isEditing && createdFlyer?.pdf_url && (
                <span style={{
                  marginLeft: '12px',
                  padding: '3px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#166534',
                  backgroundColor: '#dcfce7',
                  borderRadius: '10px',
                  verticalAlign: 'middle',
                }}>Published</span>
              )}
            </h1>

            {/* Step Progress */}
            <div className="step-indicator">
              {steps.map((step, index) => (
                <div key={step.number} className="step-item">
                  <div className="step-content">
                    <div className={`step-circle ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}>
                      {currentStep > step.number ? <CheckIcon /> : step.number}
                    </div>
                    <div className="step-info">
                      <span className={`step-name ${currentStep >= step.number ? 'active' : ''}`}>
                        {step.name}
                      </span>
                      <span className="step-description">{step.description}</span>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`step-connector ${currentStep > step.number ? 'active' : ''}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area - Steps 1 & 2 only */}
        {currentStep !== 3 && (
        <div className="wizard-content">
          {/* Step 1: Flyer Details */}
          {currentStep === 1 && (
            <div className="step-container step-1">
              <div className="form-card">
                <h2 className="form-card-title">Enter Flyer Details</h2>

                <div className="form-fields">
                  <div className="form-group">
                    <label className="form-label">Flyer Title</label>
                    <input
                      type="text"
                      className={`form-input ${errors.title ? 'error' : ''}`}
                      placeholder="e.g., Weekly Specials"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        if (errors.title) setErrors({ ...errors, title: '' });
                      }}
                    />
                    {errors.title && <span className="form-error">{errors.title}</span>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <CalendarIcon />
                        Start Date
                      </label>
                      <input
                        type="date"
                        className={`form-input ${errors.startDate ? 'error' : ''}`}
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (errors.startDate) setErrors({ ...errors, startDate: '' });
                        }}
                      />
                      {errors.startDate && <span className="form-error">{errors.startDate}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <CalendarIcon />
                        End Date
                      </label>
                      <input
                        type="date"
                        className={`form-input ${errors.endDate ? 'error' : ''}`}
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          if (errors.endDate) setErrors({ ...errors, endDate: '' });
                        }}
                      />
                      {errors.endDate && <span className="form-error">{errors.endDate}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="action-btn secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button className="action-btn primary" onClick={handleStep1Next}>
                    Continue
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Select Template */}
          {currentStep === 2 && (
            <div className="step-container step-2">
              <div className="form-card wide">
                <h2 className="form-card-title">Select a Template</h2>

                {flyerTemplates.length === 0 ? (
                  <div className="empty-templates">
                    <p>No flyer templates available.</p>
                    <button
                      className="create-template-link"
                      onClick={() => navigate('/flyertemplatemanager')}
                    >
                      Create Flyer Template
                    </button>
                  </div>
                ) : (
                  <div className="templates-grid">
                    {flyerTemplates.map((template) => (
                      <div
                        key={template._id || template.id}
                        className={`template-card ${highlightedTemplateId === (template._id || template.id) ? 'selected' : ''}`}
                        onClick={() => handleTemplateSelect(template._id || template.id)}
                      >
                        <div className="template-preview-wrapper">
                          <FlyerTemplatePreview template={template} />
                          {highlightedTemplateId === (template._id || template.id) && (
                            <div className="template-selected-badge">
                              <CheckIcon />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-actions">
                  <button className="action-btn secondary" onClick={() => setCurrentStep(1)}>
                    <ChevronLeftIcon />
                    Back
                  </button>
                  <button
                    className="action-btn primary"
                    onClick={handleStep2Next}
                    disabled={!highlightedTemplateId || isCreating}
                  >
                    {isCreating ? 'Creating...' : 'Create Flyer'}
                    {!isCreating && <ChevronRightIcon />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Step 3: Add Products - Full Editor Layout (outside wizard-content for full width) */}
        {currentStep === 3 && createdFlyer && (
          <div className="flyer-editor-wrapper">
              {/* Toolbar Header */}
              <div className="editor-toolbar">
                <div className="toolbar-section">
                  <span className="toolbar-label">Title:</span>
                  <span className="toolbar-value">{createdFlyer.flyer_title}</span>
                </div>
                <div className="toolbar-divider" />
                <div className="toolbar-section">
                  <CalendarIcon />
                  <span className="toolbar-value">
                    {createdFlyer.start_date?.toLocaleDateString()} - {createdFlyer.end_date?.toLocaleDateString()}
                  </span>
                </div>

                <div className="toolbar-spacer" />

                {/* Page Navigation - only show if multi-page flyer */}
                {flyerTotalPages > 1 && (
                  <>
                    <div className="toolbar-section page-navigation">
                      <button
                        onClick={() => setFlyerPageIndex(Math.max(0, flyerPageIndex - 1))}
                        disabled={flyerPageIndex === 0}
                        className="nav-btn"
                        title="Previous Page"
                      >
                        <ChevronLeftIcon />
                      </button>
                      <span className="page-indicator">
                        Page {flyerPageIndex + 1} of {flyerTotalPages}
                      </span>
                      <button
                        onClick={() => setFlyerPageIndex(Math.min(flyerTotalPages - 1, flyerPageIndex + 1))}
                        disabled={flyerPageIndex === flyerTotalPages - 1}
                        className="nav-btn"
                        title="Next Page"
                      >
                        <ChevronRightIcon />
                      </button>
                    </div>
                    <div className="toolbar-divider" />
                  </>
                )}

                {/* Zoom Controls */}
                <div className="toolbar-section zoom-controls">
                  <button
                    className="zoom-btn"
                    onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                    disabled={zoom <= 0.5}
                    title="Zoom Out"
                  >
                    −
                  </button>
                  <input
                    type="range"
                    className="zoom-slider"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                  />
                  <span className="zoom-level">{Math.round(zoom * 100)}%</span>
                  <button
                    className="zoom-btn"
                    onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                    disabled={zoom >= 2}
                    title="Zoom In"
                  >
                    +
                  </button>
                </div>

                {/* Guide Controls */}
                <div className="toolbar-section guide-controls">
                  <button
                    className={`guide-btn ${showCenterGuides ? 'active' : ''}`}
                    onClick={() => setShowCenterGuides(!showCenterGuides)}
                    title="Toggle Center Guidelines"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2">
                      <line x1="12" y1="2" x2="12" y2="22" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="editor-content">
                {/* Canvas Area */}
                <div className="canvas-area">
                  <div className="canvas-wrapper" style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}>
                    <Flyer
                      ref={flyerRef}
                      templateId={selectedTemplate}
                      createdFlyer={createdFlyer}
                      setCreatedFlyer={setCreatedFlyer}
                      style={{ zIndex: isProductDragging ? 10 : 1 }}
                      showCenterGuides={showCenterGuides}
                      currentPageIndex={flyerPageIndex}
                      onPageCountChange={setFlyerTotalPages}
                      selectedCellProduct={selectedCellProduct}
                      onSelectCellProduct={handleSelectCellProduct}
                    />
                  </div>
                </div>

                {/* Right Sidebar - Tabbed */}
                <div className="products-sidebar">
                  {/* Tab Switcher */}
                  <div className="sidebar-tabs">
                    <button
                      className={`sidebar-tab ${sidebarTab === 'search' ? 'active' : ''}`}
                      onClick={() => setSidebarTab('search')}
                    >
                      <SearchIcon /> Search
                    </button>
                    <button
                      className={`sidebar-tab ${sidebarTab === 'upload' ? 'active' : ''}`}
                      onClick={() => setSidebarTab('upload')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="17,8 12,3 7,8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Upload
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="sidebar-tab-content">
                    {/* Search Tab */}
                    {sidebarTab === 'search' && (
                      <div className="search-tab">
                        <form className="search-form" onSubmit={handleSearchSubmit}>
                          <div className="search-input-wrapper">
                            <SearchIcon />
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search products..."
                              value={searchKeywords}
                              onChange={(e) => setSearchKeywords(e.target.value)}
                            />
                          </div>
                          <button type="submit" className="search-btn" disabled={isSearching}>
                            {isSearching ? 'Searching...' : 'Search'}
                          </button>
                        </form>

                        {selectedCellProduct && (
                          <div className="selection-hint">
                            <span>Select a product to replace</span>
                            <button onClick={() => setSelectedCellProduct(null)}>×</button>
                          </div>
                        )}

                        {searchResultsCount !== null && (
                          <p className="results-count desktop-only">
                            {searchResultsCount} Products found.
                          </p>
                        )}

                        {searchResultsCount > 0 && (
                          <div className="products-list desktop-only">
                            <div className="product-cards">
                              {getCurrentPageProducts().map((product, index) => (
                                <ProductCard
                                  key={index}
                                  product={product}
                                  onDoubleClick={handleReplaceCellProduct}
                                  hasSelection={!!selectedCellProduct}
                                  onAddToStaging={addToStaging}
                                  isStaged={stagedProducts.some(p => (p.retailer_sku_id || p.id) === product.retailer_sku_id)}
                                />
                              ))}
                            </div>

                            <div className="pagination">
                              <button className="pagination-btn" disabled={currentPage === 1} onClick={prevPage}>
                                <ChevronLeftIcon /> Prev
                              </button>
                              <span className="pagination-info">{currentPage} / {totalPages}</span>
                              <button className="pagination-btn" disabled={currentPage === totalPages} onClick={nextPage}>
                                Next <ChevronRightIcon />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Upload Tab */}
                    {sidebarTab === 'upload' && (
                      <UploadPlansTab
                        files={uploadFiles}
                        onAddFiles={addUploadFiles}
                        onRemoveFile={removeUploadFile}
                        onClearFiles={clearUploadFiles}
                        isExtracting={isExtracting}
                        onExtract={extractProducts}
                        extractedProducts={extractedProducts}
                        onAddToStaging={addToStaging}
                        onAddAllToStaging={addAllToStaging}
                        onResolveProduct={resolveProduct}
                        onResolveGlobalProduct={resolveGlobalProduct}
                        onDismissCandidates={dismissCandidates}
                        onAddToCatalog={addToCatalog}
                        error={extractionError}
                      />
                    )}
                  </div>

                  {/* Staging Area - Always visible */}
                  <StagingArea
                    products={stagedProducts}
                    onRemove={removeFromStaging}
                    onClear={clearStaging}
                    onAutoFill={handleAutoFill}
                    isAutoFilling={isAutoFilling}
                  />
                </div>
              </div>

              {/* Mobile Footer Container - wraps search results and action bar */}
              <div className="mobile-footer-container">
                {/* Mobile Search Results Overlay - positioned above action bar */}
                {searchResultsCount > 0 && showSearchResults && (
                  <div className="search-results-overlay mobile-only">
                    <div className="search-results-header">
                      <span className="results-count-mobile">{searchResultsCount} Products</span>
                      <button className="close-results-btn" onClick={handleCloseSearchResults}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                    <div className="product-cards">
                      {getCurrentPageProducts().map((product, index) => (
                        <ProductCard
                          key={index}
                          product={product}
                          onDoubleClick={handleReplaceCellProduct}
                          hasSelection={!!selectedCellProduct}
                          onAddToStaging={addToStaging}
                          isStaged={stagedProducts.some(p => (p.retailer_sku_id || p.id) === product.retailer_sku_id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Bottom Action Bar */}
                <div className="action-bar">
              <button className="action-btn secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button className="action-btn primary-outline" onClick={handleDownload}>
                Download
              </button>
              <button className="action-btn primary" onClick={handlePublish}>
                Publish
              </button>
              <button className="action-btn primary" onClick={handleSave}>
                {isEditing ? 'Update' : 'Save'}
              </button>
              <button className="action-btn primary-dark" onClick={handleSaveAndClose}>
                {isEditing ? 'Update & Close' : 'Save & Close'}
              </button>
              {isEditing && (
                <button className="action-btn danger" onClick={handleDelete}>
                  Delete
                </button>
              )}
                </div>
              </div>
          </div>
        )}
      </div>
    </DragContext.Provider>
  );
}

export default AddFlyer;
